import { DISCOVERY_SKIPPED } from "../discoverySources";
import type { DiscoverySource } from "../discoverySources";
import { setToken, req } from "./transport";
import type { AuthUser, CompanyContext } from "./types";
import { INVALID_LOGIN_MESSAGE, isDesktopAdminClient } from "../clientSurface";

export const accountApi = {
  async signup(input: { firstName: string; lastName: string; email: string; password: string; activities?: string[]; referralCode?: string; analyticsBrowserKey?: string; onboardingConsent?: { dataSharing: true; riskWaiver: true; accuracy: true } }) {
    const r = await req<{ user: AuthUser; token: string }>("/auth/signup", { method: "POST", body: JSON.stringify(input) });
    if (r?.token) await setToken(r.token);
    return r?.user ?? null;
  },

  async getMyReferral() {
    return req<{ code: string; referrals: { id: string; refereeName: string; status: string; createdAt: string }[]; totalEarned: number }>("/referral/me");
  },

  async validateReferralCode(code: string) {
    return req<{ valid: boolean }>(`/referral/validate?code=${encodeURIComponent(code)}`);
  },

  async login(input: { email: string; password: string; rememberMe?: boolean }) {
    const r = await req<{ user: AuthUser; token: string }>("/auth/login", { method: "POST", body: JSON.stringify(input) });
    // A response can arrive after resizing, or from an older server. Never
    // retain its Admin token on an unsupported client; revoke the issued cookie.
    if (r?.user.userType === "admin" && !isDesktopAdminClient()) {
      try { await accountApi.logout(); }
      finally { throw new Error(INVALID_LOGIN_MESSAGE); }
    }
    if (r?.token) await setToken(r.token);
    return r?.user ?? null;
  },

  async logout() { await req("/auth/logout", { method: "POST" }); await setToken(null); },

  async me() {
    const r = await req<{ user: AuthUser }>("/auth/me");
    return r?.user?.userType === "admin" && !isDesktopAdminClient() ? null : r?.user ?? null;
  },

  async addActivity(activity: string) {
    const r = await req<{ user: AuthUser }>("/auth/activities", { method: "POST", body: JSON.stringify({ activity }) });
    return r?.user ?? null;
  },

  async addActivities(activities: string[]) {
    const r = await req<{ user: AuthUser }>("/auth/activities", { method: "POST", body: JSON.stringify({ activities }) });
    return r?.user ?? null;
  },

  async switchContext(context: string | null) {
    const r = await req<{ user: AuthUser }>("/auth/context", { method: "POST", body: JSON.stringify({ context }) });
    return r?.user ?? null;
  },

  async getContexts() {
    return req<{ active: string | null; personal: { key: string; label: string }[]; companies: CompanyContext[]; historyCompanies?: CompanyContext[] }>("/auth/contexts");
  },

  // Increment 23: email verification
  async sendVerifyEmail() {
    return req<{ ok: boolean; alreadyVerified?: boolean; verifyUrl?: string | null }>("/auth/verify/send", { method: "POST" });
  },

  async confirmVerifyEmail(token: string) {
    return req<{ ok: boolean; user?: AuthUser }>("/auth/verify/confirm", { method: "POST", body: JSON.stringify({ token }) });
  },

  // QA #50 — email OTP verification (code, sent from SNP-domain sender)
  async sendEmailOtp() {
    return req<{ ok: boolean; alreadyVerified?: boolean; devCode?: string }>("/auth/otp/send", { method: "POST" });
  },

  async verifyEmailOtp(code: string) {
    return req<{ ok: boolean; user?: AuthUser }>("/auth/otp/verify", { method: "POST", body: JSON.stringify({ code }) });
  },

  // Batch item 1: password reset (logged-out self-recovery)
  async forgotPassword(email: string) {
    return req<{ ok: boolean }>("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
  },

  async resetPassword(token: string, password: string) {
    return req<{ ok: boolean; error?: string }>("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
  },

  // Increment 24: change email
  async requestEmailChange(newEmail: string, password: string) {
    return req<{ ok: boolean; pendingEmail?: string; confirmUrl?: string | null }>("/auth/email/change", { method: "POST", body: JSON.stringify({ newEmail, password }) });
  },

  async confirmEmailChange(token: string) {
    return req<{ ok: boolean; user?: AuthUser; email?: string }>("/auth/email/confirm", { method: "POST", body: JSON.stringify({ token }) });
  },

  // Increment 35: email notification preference
  async getEmailPrefs() {
    return req<{ emailNotifications: boolean }>("/auth/email-prefs");
  },

  async setEmailPrefs(emailNotifications: boolean) {
    return req<{ ok: boolean; emailNotifications: boolean }>("/auth/email-prefs", { method: "PUT", body: JSON.stringify({ emailNotifications }) });
  },

  // Increment 47: identity edit + admin override
  async updateMyName(firstName: string, lastName: string) {
    return req<{ ok: boolean; reKycTriggered: boolean; user: AuthUser }>("/auth/profile", { method: "PATCH", body: JSON.stringify({ firstName, lastName }) });
  },

  // Signup acceptance, legacy acknowledgement recovery and optional discovery.
  async onboardingStatus() { return req<{ needsConsent: boolean; consentVersion: number; discoveryPromptPending: boolean }>("/auth/onboarding-status"); },

  async acceptConsent(body: { dataSharing: true; riskWaiver: true; accuracy: true; discoverySources?: DiscoverySource[] | [typeof DISCOVERY_SKIPPED] }) {
    return req<{ ok: boolean; needsConsent: boolean }>("/auth/accept-consent", { method: "POST", body: JSON.stringify(body) });
  },

  async saveDiscovery(discoverySources: DiscoverySource[] | [typeof DISCOVERY_SKIPPED]) {
    return req<{ ok: boolean }>("/auth/discovery", { method: "POST", body: JSON.stringify({ discoverySources }) });
  },

  // Increment 18: settings + saved items
  async changePassword(currentPassword: string, newPassword: string) {
    return req<{ ok: boolean }>("/auth/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
  }
};

export const ACTIVITY_LABELS: Record<string, string> = {
  find_work: "Find Work", offer_service: "Offer a Service", promote_company: "Promote a Company",
  hire: "Hire Someone", post_job: "Post a Job", post_project: "Post a Project",
};
